import React, { createContext, ReactNode, useContext, useState } from 'react';
import type { BusinessToIncludeType } from '@/services/sequencesService';
import type { SequencesType, StepsType } from './hooks/useSequences';
import type { TemplatesType } from './hooks/useTemplates';
import type { EmailTemplateType } from './hooks/useEmailTemplates';
import type { BusinessListType } from './hooks/useBusinessLists';
import type { Business } from '../dashboard/dashboardTypes';

type SequencesContextProps = {
	children: ReactNode;
};

export type TabsOptionsType = 'SEQUENCE' | 'BUSINESSES';

export const sequenceStates = [
	'MY-SEQUENCES',
	'SEQUENCE-START',
	'STEPS-CREATION',
	'ADD-BUSINESS'
] as const;

export type SequencePagesType = (typeof sequenceStates)[number];

export type ModifiedEmailsType = {
	businessId: string;
	subject: string;
	body: string;
	email?: string;
};

export type AiSequenceDialogType = {
	offering: string;
	targetAudience: string;
	valueProposition: string;
	signature?: string;
};

type AllBusinessesSelectType = 'SELECTED_ALL' | 'UNSELECTED_ALL';

type SequencesContextValue = {
	sequencePage: SequencePagesType;
	setSequencePage: (x: SequencePagesType) => void;
	sequenceEnroll: boolean;
	setSequenceEnroll: (x: boolean) => void;
	activeTab: TabsOptionsType;
	setActiveTab: (x: TabsOptionsType) => void;
	selectedSequence: SequencesType | undefined;
	setSelectedSequence: (x: SequencesType | undefined) => void;
	sequenceSearch: string;
	setSequenceSearch: (x: string) => void;
	currentPage: number;
	setCurrentPage: (x: number) => void;
	isSequencesLoading: boolean;
	setIsSequencesLoading: (x: boolean) => void;
	sequences: SequencesType[] | undefined;
	setSequences: (x: SequencesType[] | undefined) => void;
	templates: TemplatesType[] | undefined;
	setTemplates: (x: TemplatesType[] | undefined) => void;
	selectedTemplate: TemplatesType | undefined;
	setSelectedTemplate: (x: TemplatesType | undefined) => void;
	setAiSequenceModalFields: (x: AiSequenceDialogType | undefined) => void;
	aiSequenceModalFields: AiSequenceDialogType | undefined;
	selectedStep: StepsType | undefined;
	setSelectedStep: (x: StepsType | undefined) => void;
	emailTemplates: EmailTemplateType[] | undefined;
	setEmailTemplates: (x: EmailTemplateType[]) => void;
	businessLists: BusinessListType[];
	setBusinessLists: (x: BusinessListType[]) => void;
	selectedLists: BusinessListType[];
	setSelectedLists: (x: BusinessListType[]) => void;
	businessesInLists: Business[];
	setBusinessesInLists: (x: Business[]) => void;
	selectedBusinesses: Business[];
	setSelectedBusinesses: (x: Business[]) => void;
	unselectedBusinesses: string[];
	setUnselectedBusinesses: (x: string[]) => void;
	businessesToRemove: string[];
	setBusinessesToRemove: (x: string[]) => void;
	businessesToInclude: BusinessToIncludeType[];
	setBusinessesToInclude: (x: BusinessToIncludeType[]) => void;
	showSendAll: boolean;
	setShowSendAll: (x: boolean) => void;
	businessesScrollPage: number;
	setBusinessesScrollPage: (x: number) => void;
	hasMoreBusinessesToFetch: boolean;
	setHasMoreBusinessesToFetch: (x: boolean) => void;
	modifiedEmails: ModifiedEmailsType[];
	setModifiedEmails: (x: ModifiedEmailsType[]) => void;
	unapprovedBusinesses: string[];
	setUnapprovedBusinesses: (x: string[]) => void;
	businessesAlreadyInSequence: Set<string>;
	setBusinessesAlreadyInSequence: (x: Set<string>) => void;
	allBusinessesSelect: AllBusinessesSelectType;
	setAllBusinessesSelect: (x: AllBusinessesSelectType) => void;
	isEnrollOpen: boolean;
	setIsEnrollOpen: (x: boolean) => void;
	isBusinessesLoading: boolean;
	setIsBusinessesLoading: (x: boolean) => void;
	showAiSequenceModal: boolean;
	setShowAiSequenceModal: (x: boolean) => void;
};

const SequencesContext = createContext<SequencesContextValue | undefined>(
	undefined
);

const SequencesContextProvider = ({ children }: SequencesContextProps) => {
	const [sequencePage, setSequencePage] =
		useState<SequencePagesType>('MY-SEQUENCES');
	const [sequenceEnroll, setSequenceEnroll] = useState(false);
	const [selectedTemplate, setSelectedTemplate] = useState<TemplatesType>();
	const [aiSequenceModalFields, setAiSequenceModalFields] = useState<
		AiSequenceDialogType | undefined
	>();

	const [activeTab, setActiveTab] = useState<TabsOptionsType>('SEQUENCE');
	const [selectedSequence, setSelectedSequence] = useState<
		SequencesType | undefined
	>();
	const [sequences, setSequences] = useState<SequencesType[] | undefined>([]);
	const [sequenceSearch, setSequenceSearch] = useState<string>('');
	const [currentPage, setCurrentPage] = useState(1);
	const [isSequencesLoading, setIsSequencesLoading] = useState(false);
	const [templates, setTemplates] = useState<TemplatesType[]>();
	const [selectedStep, setSelectedStep] = useState<StepsType>();
	const [emailTemplates, setEmailTemplates] = useState<EmailTemplateType[]>();
	const [businessLists, setBusinessLists] = useState<BusinessListType[]>([]);
	const [selectedLists, setSelectedLists] = useState<BusinessListType[]>([]);
	const [businessesInLists, setBusinessesInLists] = useState<Business[]>([]);
	const [selectedBusinesses, setSelectedBusinesses] = useState<Business[]>(
		[]
	);
	const [unselectedBusinesses, setUnselectedBusinesses] = useState<string[]>([
		'none'
	]);
	const [businessesToRemove, setBusinessesToRemove] = useState<string[]>([]);
	const [businessesToInclude, setBusinessesToInclude] = useState<
		BusinessToIncludeType[]
	>([]);
	const [unapprovedBusinesses, setUnapprovedBusinesses] = useState<string[]>(
		[]
	);
	const [modifiedEmails, setModifiedEmails] = useState<ModifiedEmailsType[]>(
		[]
	);
	const [showSendAll, setShowSendAll] = useState(false);
	const [businessesScrollPage, setBusinessesScrollPage] = useState(1);

	const [hasMoreBusinessesToFetch, setHasMoreBusinessesToFetch] =
		useState(true);
	const [businessesAlreadyInSequence, setBusinessesAlreadyInSequence] =
		useState<Set<string>>(new Set());

	const [allBusinessesSelect, setAllBusinessesSelect] =
		useState<AllBusinessesSelectType>('SELECTED_ALL');

	const [isEnrollOpen, setIsEnrollOpen] = useState(false);
	const [isBusinessesLoading, setIsBusinessesLoading] =
		useState<boolean>(false);

	const [showAiSequenceModal, setShowAiSequenceModal] = useState(false);

	const contextValue = {
		sequencePage,
		setSequencePage,
		sequenceEnroll,
		setSequenceEnroll,
		sequences,
		setSequences,
		activeTab,
		setActiveTab,
		selectedSequence,
		setSelectedSequence,
		sequenceSearch,
		setSequenceSearch,
		currentPage,
		setCurrentPage,
		isSequencesLoading,
		setIsSequencesLoading,
		selectedTemplate,
		setSelectedTemplate,
		templates,
		setTemplates,
		setAiSequenceModalFields,
		aiSequenceModalFields,
		selectedStep,
		setSelectedStep,
		emailTemplates,
		setEmailTemplates,
		businessLists,
		setBusinessLists,
		selectedLists,
		setSelectedLists,
		businessesInLists,
		setBusinessesInLists,
		selectedBusinesses,
		setSelectedBusinesses,
		unselectedBusinesses,
		setUnselectedBusinesses,
		showSendAll,
		setShowSendAll,
		businessesToRemove,
		setBusinessesToRemove,
		businessesToInclude,
		setBusinessesToInclude,
		businessesScrollPage,
		setBusinessesScrollPage,
		hasMoreBusinessesToFetch,
		setHasMoreBusinessesToFetch,
		modifiedEmails,
		setModifiedEmails,
		unapprovedBusinesses,
		setUnapprovedBusinesses,
		businessesAlreadyInSequence,
		setBusinessesAlreadyInSequence,
		allBusinessesSelect,
		setAllBusinessesSelect,
		isEnrollOpen,
		setIsEnrollOpen,
		isBusinessesLoading,
		setIsBusinessesLoading,
		showAiSequenceModal,
		setShowAiSequenceModal
	};

	return (
		<SequencesContext.Provider value={contextValue}>
			{children}
		</SequencesContext.Provider>
	);
};

export default SequencesContextProvider;

export const useSequencesContext = () => {
	const context = useContext(SequencesContext);

	if (!context) {
		throw new Error(
			'useSequencesContext must be used within a SequencesProvider'
		);
	}
	return context;
};
